// A sideways strip that feels native on a phone (app-feel plan, builder B).
//
// Used by the Assistant's tab row and its numbers strip. On a phone: snap
// scrolling, a soft fade on the edge that has more, and the active item
// scrolled into view. From `md:` up it is a plain row, as before.
//
// ⚠️ THE LEFT 24 PX BELONG TO THE SHELL'S SWIPE-BACK (shared contract). A
// native scroller cannot be told to ignore a touch after it started, so the
// strip simply does not START there: on a phone it is inset (`ml-2` on top of
// the page's 16 px gutter = 24 px), and it bleeds off the right edge only.
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

export function SwipeStrip({ children, activeKey, label, className = '', role }: {
  children: ReactNode;
  /** The `data-strip-key` of the item to keep in view. */
  activeKey?: string | null;
  label: string;
  className?: string;
  role?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const measure = () => {
    const el = ref.current;
    if (!el) return;
    const start = el.scrollLeft > 2;
    const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setEdges((e) => (e.start === start && e.end === end ? e : { start, end }));
  };

  useLayoutEffect(measure);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The active tab is never hidden off the edge — e.g. « Réglages » opened from a link.
  useEffect(() => {
    const el = ref.current;
    if (!el || !activeKey) return;
    const item = el.querySelector<HTMLElement>(`[data-strip-key="${typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(activeKey) : activeKey}"]`);
    if (!item || typeof item.scrollIntoView !== 'function') return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    item.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduce ? 'auto' : 'smooth' });
  }, [activeKey]);

  // The fade says « more this way » — only on the side that has more.
  const mask = `linear-gradient(to right, ${edges.start ? 'transparent 0, #000 24px' : '#000 0'}, ${edges.end ? '#000 calc(100% - 32px), transparent 100%' : '#000 100%'})`;
  return (
    <div
      ref={ref}
      role={role}
      aria-label={label}
      onScroll={measure}
      data-strip-edges={`${edges.start ? 'start' : ''}${edges.end ? 'end' : ''}` || 'none'}
      className={`flex overflow-x-auto overscroll-x-contain snap-x snap-mandatory scroll-px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ml-2 -mr-4 pr-4 md:ml-0 md:mr-0 md:pr-0 md:snap-none ${className}`}
      style={{ WebkitMaskImage: mask, maskImage: mask }}
    >
      {children}
    </div>
  );
}
